import {
  ReactNode,
  useCallback,
  useMemo,
  useState,
  forwardRef,
  ComponentPropsWithoutRef,
  HTMLAttributes,
} from 'react';
import { PopoverTriggerProps } from '@radix-ui/react-popover';
import { UseInfiniteScrollHookResult } from 'react-infinite-scroll-hook';
import { useTranslation } from '@sib/i18n';
import { ChevronDown, Search } from '../../icons';
import { cn, filterOptionsByLabel } from '../../utils';
import { Accordion } from '../Accordion';
import { Button } from '../Button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandList,
} from '../Command';
import { containerVariants } from '../Input';
import { Loader } from '../Loader';
import { Popover, PopoverContent, PopoverTrigger } from '../Popover';
import { Skeleton } from '../Skeleton';
import { ComboboxCollapsibleSection } from './ComboboxCollapsibleSection';
import { ComboboxCommandItem } from './ComboboxCommandItem';
import {
  ComboboxMultiSelectValue,
  MultiSelectValueVariant,
} from './ComboboxMultiSelectValue';

export type ComboboxOption<T extends Record<string, unknown> = {}> = T & {
  value: string;
  secondaryValue?: string;
  label: string | ReactNode;
  isCollapsible?: boolean;
};
export interface ComboboxCategoryOption<T extends Record<string, unknown> = {}>
  extends ComboboxOption {
  categoryItems?: ComboboxOption<T>[];
  totalItems?: number;
  sentryRef?: UseInfiniteScrollHookResult[0];
  hasNextPage?: boolean;
  isLoading?: boolean;
}

export type ComboboxProps<
  V extends string | string[] | null = string,
  T extends Record<string, unknown> = {},
> = {
  options: ComboboxOption<T>[];
  value?: V;
  onValueChange?: (value: V | null) => void;
  OptionComponent: (o: ComboboxOption<T>) => ReactNode;
  hasNextPage?: boolean;
  editable?: boolean;
  isLoading?: boolean;
  isFetchingNextPage?: boolean;
  disabled?: boolean;
  readOnly?: boolean;
  placeholder?: string;
  emptyMessage?: string;
  allowNullValue?: boolean;
  clearText?: string;
  noResultMessage?: string;
  onInputValueChange?: (value: string) => void;
  searchValue?: string;
  onOptionSelect?: (o: ComboboxOption<T>) => void | boolean;
  searchPlaceholder?: string;
  sentryRef?: UseInfiniteScrollHookResult['0'];
  Header?: ReactNode;
  Footer?: ReactNode;
  isMultiSelect?: boolean;
  multiSelectValueVariant?: MultiSelectValueVariant;
  hideSearch?: boolean;
  /** Filters options by label on the client while typing; for lists loaded in full. */
  filterLocally?: boolean;
  categoryPlaceholder?: string;
  showEmpty?: boolean;
  visibleOptionsCount?: number;
  onBack?: () => void;
  renderValue?: (selected: ComboboxOption<T>[], value: V) => ReactNode;
  customMuliSelectLabels?: ReactNode;
  onClose?: () => void;
  PopoverTriggerProps?: Partial<PopoverTriggerProps> & {
    'data-testid'?: string;
  };
  onOpenChange?: (open: boolean) => void;
  CommandListProps?: ComponentPropsWithoutRef<typeof CommandList>;
  hideNoResults?: boolean;
  HeaderWrapperProps?: HTMLAttributes<HTMLDivElement>;
  PopoverContentProps?: ComponentPropsWithoutRef<typeof PopoverContent>;
  ContainerProps?: HTMLAttributes<HTMLDivElement>;
};

const BaseCombobox = <
  V extends string | string[] | null = string,
  T extends Record<string, unknown> = {},
>(
  {
    options,
    value,
    onValueChange,
    OptionComponent,
    hasNextPage,
    isLoading,
    isFetchingNextPage,
    disabled,
    readOnly,
    emptyMessage,
    allowNullValue,
    searchPlaceholder,
    clearText = 'Clear',
    noResultMessage,
    onInputValueChange,
    onOptionSelect,
    sentryRef,
    Header,
    Footer,
    isMultiSelect,
    multiSelectValueVariant = 'default',
    hideSearch,
    filterLocally = false,
    renderValue,
    searchValue,
    visibleOptionsCount = 2,
    showEmpty = false,
    customMuliSelectLabels,
    onClose,
    PopoverTriggerProps,
    onOpenChange,
    CommandListProps,
    hideNoResults = false,
    HeaderWrapperProps,
    placeholder,
    PopoverContentProps,
    ContainerProps,
  }: ComboboxProps<V, T>,
  ref: React.Ref<HTMLDivElement>
) => {
  const { t } = useTranslation();
  const resolvedSearchPlaceholder = searchPlaceholder ?? t('search');
  const resolvedNoResultMessage = noResultMessage ?? t('noResults');
  const [open, setOpen] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [expandedCategories, setExpandedCategories] = useState<string[]>([]);

  const selectedOption = useMemo(() => {
    if (
      !value ||
      (isMultiSelect && Array.isArray(value) && value.length === 0) ||
      typeof value !== 'string'
    )
      return null;

    return options.find(o => o.value === value) ?? null;
  }, [options, value, isMultiSelect]);

  const allSelectedOptions = useMemo(() => {
    if (!isMultiSelect || !Array.isArray(value) || value.length === 0)
      return [];

    return options.filter(opt => value.includes(opt.value as string));
  }, [isMultiSelect, value, options]);

  const collapsibleOptions = useMemo(
    () => options.filter(opt => opt.isCollapsible),
    [options]
  );

  const regularOptions = useMemo(() => {
    const nonCollapsibleOptions = options.filter(opt => !opt.isCollapsible);

    //TODO: Adjust the BE endpoints to accept searchTerm so we don't filter on FE side
    return filterLocally
      ? filterOptionsByLabel(nonCollapsibleOptions, inputValue)
      : nonCollapsibleOptions;
  }, [options, filterLocally, inputValue]);

  const handleInputChange = useCallback(
    (value: string) => {
      setInputValue(value);
      onInputValueChange?.(value);
    },
    [onInputValueChange]
  );

  const handleOpenChange = useCallback(
    (nextOpen: boolean) => {
      setOpen(nextOpen);
      if (filterLocally && !nextOpen) setInputValue('');
    },
    [filterLocally]
  );

  const handleOptionSelect = useCallback(
    (option: ComboboxOption<T>) => onOptionSelect?.(option),
    [onOptionSelect]
  );

  const toggleValue = useCallback(
    (valueToToggle: string) => {
      if (!onValueChange) return;

      if (!isMultiSelect) return onValueChange(valueToToggle as V);

      const arr = (value as string[]) ?? [];
      const next = arr.includes(valueToToggle)
        ? arr.filter(x => x !== valueToToggle)
        : [...arr, valueToToggle];

      onValueChange(next as V);
    },
    [isMultiSelect, onValueChange, value]
  );

  const handleRemoveItem = useCallback(
    (itemValue: string, e: React.SyntheticEvent) => {
      e.stopPropagation();
      if (!onValueChange || !isMultiSelect) return;

      const arr = (value as string[]) ?? [];
      const next = arr.filter(x => x !== itemValue);
      onValueChange(next as V);
    },
    [isMultiSelect, onValueChange, value]
  );

  const fieldPlaceholder =
    placeholder || emptyMessage || t('filters.selectPlaceholder');
  const isInteractionDisabled = disabled || readOnly;
  const hasMultiSelectValue =
    isMultiSelect && Array.isArray(value) && value.length > 0;
  const useExpandableMultiSelectLayout =
    hasMultiSelectValue &&
    (multiSelectValueVariant === 'pills' || Boolean(renderValue));

  const renderMultiSelectValue = () => {
    if (!hasMultiSelectValue) return null;

    const customValue = renderValue?.(allSelectedOptions, value as V);

    if (customValue != null) return customValue;

    if (allSelectedOptions.length)
      return (
        <ComboboxMultiSelectValue
          options={allSelectedOptions}
          variant={multiSelectValueVariant}
          visibleOptionsCount={visibleOptionsCount}
          onRemoveItem={handleRemoveItem}
          readOnly={readOnly}
        />
      );

    return <span className="text-muted-foreground">{fieldPlaceholder}</span>;
  };

  return (
    <Popover
      modal
      open={open && !isInteractionDisabled}
      onOpenChange={open => {
        handleOpenChange(open);
        onOpenChange?.(open);
        if (!open) onClose?.();
      }}
    >
      <div
        ref={ref}
        {...ContainerProps}
        className={cn('relative', ContainerProps?.className)}
      >
        {allowNullValue && (
          <Button
            size="custom"
            variant="ghost"
            type="button"
            disabled={isInteractionDisabled}
            className={cn(
              'absolute -top-6 right-0 w-full justify-end',
              isInteractionDisabled &&
                'pointer-events-auto cursor-not-allowed opacity-50'
            )}
            onClick={() => {
              onValueChange?.(null);
              handleInputChange('');
            }}
          >
            {clearText}
          </Button>
        )}
        <PopoverTrigger asChild>
          <button
            role="combobox"
            aria-expanded={open}
            disabled={isInteractionDisabled || isLoading}
            {...PopoverTriggerProps}
            className={cn(
              containerVariants({ variant: 'base' }),
              'flex w-full cursor-pointer justify-between px-3 py-2',
              useExpandableMultiSelectLayout &&
                'h-auto min-h-10 min-w-0 items-center overflow-hidden',
              isInteractionDisabled &&
                'pointer-events-auto cursor-not-allowed opacity-50',
              PopoverTriggerProps?.className
            )}
          >
            {renderValue && !isMultiSelect && value ? (
              renderValue(selectedOption ? [selectedOption] : [], value)
            ) : isMultiSelect && !customMuliSelectLabels ? (
              hasMultiSelectValue ? (
                renderMultiSelectValue()
              ) : Array.isArray(value) && showEmpty && renderValue ? (
                renderValue(allSelectedOptions, value)
              ) : (
                <span className="text-muted-foreground">
                  {fieldPlaceholder}
                </span>
              )
            ) : isMultiSelect && customMuliSelectLabels ? (
              value?.length ? (
                <span className="truncate">{customMuliSelectLabels}</span>
              ) : (
                <span className="text-muted-foreground">
                  {fieldPlaceholder}
                </span>
              )
            ) : selectedOption ? (
              renderValue && value ? (
                renderValue([selectedOption], value)
              ) : (
                <OptionComponent {...selectedOption} />
              )
            ) : (
              <span className="text-muted-foreground">{fieldPlaceholder}</span>
            )}

            {isLoading ? (
              <Loader />
            ) : (
              <ChevronDown className="ml-2 h-4 w-4 shrink-0" />
            )}
          </button>
        </PopoverTrigger>

        <PopoverContent
          side="bottom"
          className={cn(
            'w-(--radix-popover-trigger-width) p-0',
            PopoverContentProps?.className
          )}
          {...PopoverContentProps}
        >
          <Command shouldFilter={false}>
            {!hideSearch && (
              <div className="border-b px-4 py-2">
                <CommandInput
                  value={
                    typeof searchValue === 'string' ? searchValue : inputValue
                  }
                  onValueChange={handleInputChange}
                  placeholder={resolvedSearchPlaceholder}
                  startAdornment={
                    <Search className="text-muted-foreground size-5" />
                  }
                  containerClassName="border-none p-0"
                />
              </div>
            )}

            {Header && (
              <div
                {...HeaderWrapperProps}
                className={cn(
                  'border-b px-4 py-2',
                  HeaderWrapperProps?.className
                )}
              >
                {Header}
              </div>
            )}
            <CommandList {...CommandListProps}>
              <CommandGroup>
                {collapsibleOptions.length > 0 && (
                  <Accordion
                    type="multiple"
                    value={expandedCategories}
                    onValueChange={setExpandedCategories}
                    className="w-full"
                  >
                    {collapsibleOptions.map((opt, index) => (
                      <ComboboxCollapsibleSection<V, T>
                        key={`${opt.value}-${index}`}
                        option={opt}
                        isMultiSelect={isMultiSelect}
                        value={value}
                        toggleValue={toggleValue}
                        onOptionSelect={handleOptionSelect}
                        setOpen={handleOpenChange}
                        OptionComponent={OptionComponent}
                        noResultMessage={resolvedNoResultMessage}
                      />
                    ))}
                  </Accordion>
                )}
                {regularOptions.map((opt, index) => (
                  <ComboboxCommandItem<V, T>
                    key={`${opt.value}-${index}`}
                    option={opt}
                    isMultiSelect={isMultiSelect}
                    value={value}
                    toggleValue={toggleValue}
                    onOptionSelect={handleOptionSelect}
                    setOpen={handleOpenChange}
                    OptionComponent={OptionComponent}
                  />
                ))}
              </CommandGroup>

              {sentryRef && hasNextPage && (
                <div
                  ref={sentryRef}
                  className="flex w-full items-center justify-center py-2"
                >
                  {isFetchingNextPage ? (
                    <Loader />
                  ) : (
                    <Skeleton className="h-8 w-full" />
                  )}
                </div>
              )}

              {!isLoading && !hideNoResults && (
                <CommandEmpty className="px-2 py-3 text-center text-sm">
                  {resolvedNoResultMessage}
                </CommandEmpty>
              )}
            </CommandList>

            {Footer && <div className="px-1">{Footer}</div>}
          </Command>
        </PopoverContent>
      </div>
    </Popover>
  );
};

export const Combobox = forwardRef(BaseCombobox) as <
  V extends string | string[] | null = string,
  T extends Record<string, unknown> = {},
>(
  props: ComboboxProps<V, T> & { ref?: React.Ref<HTMLDivElement> }
) => ReturnType<typeof BaseCombobox>;
